import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import type {
  SentMailListItem,
  SendMailPayload,
  MailRecipientTarget,
  MailAttachment,
} from "../types/mail-broadcast.types";
import { mailBroadcastApi } from "../services/mail-broadcast.api";
import { virtualClassroomsApi } from "@/features/education-catalog/services/virtual-classrooms.api";
import SendMailModal from "../components/SendMailModal";
import styles from "./MailBroadcastPage.module.scss";

const VALID_TARGETS: MailRecipientTarget[] = [
  "all",
  "staff",
  "roles",
  "departments",
];

const MailBroadcastPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canSend = hasPermission(PERMISSIONS.EMAIL_BROADCAST_SEND);

  const [rows, setRows] = useState<SentMailListItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Diğer sayfalardan (örn. sanal sınıf modal'ı) navigate ile gelen prefill —
  // ?autoOpen=1&target=staff&prefillStaffIds=<csv>
  const [searchParams, setSearchParams] = useSearchParams();
  const [prefillTarget, setPrefillTarget] = useState<
    MailRecipientTarget | undefined
  >(undefined);
  const [prefillStaffIds, setPrefillStaffIds] = useState<string[] | undefined>(
    undefined,
  );
  const [prefillAttachments, setPrefillAttachments] = useState<
    MailAttachment[] | undefined
  >(undefined);
  const [preparingAttachments, setPreparingAttachments] = useState(false);

  useEffect(() => {
    if (searchParams.get("autoOpen") !== "1") return;
    if (!canSend) return;
    const targetParam = searchParams.get("target") ?? "";
    const staffParam = searchParams.get("prefillStaffIds") ?? "";
    if (VALID_TARGETS.includes(targetParam as MailRecipientTarget)) {
      setPrefillTarget(targetParam as MailRecipientTarget);
    }
    if (staffParam) {
      const ids = staffParam
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      if (ids.length > 0) {
        setPrefillStaffIds(ids);
      }
    }
    // Sınıf mailinden gelindiyse kursiyer listesi PDF'i hazırlanıp ek olarak konur.
    const classroomId = searchParams.get("prefillClassroomId");
    if (classroomId) {
      setPreparingAttachments(true);
      virtualClassroomsApi
        .createParticipantListPdf(classroomId)
        .then((file) => {
          if (file.id) {
            setPrefillAttachments([
              {
                id: file.id,
                name: file.originalName,
                size: file.size,
                downloadUrl: file.downloadUrl,
              },
            ]);
          }
        })
        .catch(() => {
          // apiClient global toast; mail eksiz de gönderilebilir
        })
        .finally(() => setPreparingAttachments(false));
    }
    setIsSendModalOpen(true);
    // URL'yi temizle — geri tıklamada veya yenilemede tekrar tetiklenmesin.
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, canSend]);

  useEffect(() => {
    const tmr = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(tmr);
  }, [searchTerm]);

  const fetchList = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await mailBroadcastApi.getSentList({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
      });
      setRows(res.data);
      setTotalRecords(res.total);
    } catch {
      setRows([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearch]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleSendSubmit = async (payload: SendMailPayload) => {
    await mailBroadcastApi.send(payload);
    toast.success(
      t("mailBroadcast.sendSuccess"),
    );
    await fetchList();
  };

  const columns: TableColumn<SentMailListItem>[] = [
    {
      header: t("mailBroadcast.columns.sentAt"),
      accessor: (row) => row.sentAt,
      className: styles.dateColumn,
    },
    {
      header: t("mailBroadcast.columns.recipients"),
      accessor: (row) => (
        <span className={styles.messageCell} title={row.recipientSummary}>
          {row.recipientSummary}
        </span>
      ),
      className: styles.recipientColumn,
    },
    {
      header: t("mailBroadcast.columns.subject"),
      accessor: (row) => (
        <span className={styles.messageCell} title={row.subject}>
          {row.subject}
        </span>
      ),
      className: styles.subjectColumn,
    },
    {
      header: t("mailBroadcast.columns.message"),
      accessor: (row) => (
        <span className={styles.messageCell} title={row.messagePreview}>
          {row.messagePreview}
        </span>
      ),
      className: styles.messageColumn,
    },
    {
      header: t("mailBroadcast.columns.status"),
      accessor: (row) => row.status ?? "—",
      className: styles.statusColumn,
    },
  ];

  return (
    <DashboardLayout>
      <Header title={t("mailBroadcast.title")} />
      <PageHeader
        searchPlaceholder={t("mailBroadcast.searchPlaceholder")}
        searchValue={searchTerm}
        onSearch={setSearchTerm}
        onAddNew={canSend ? () => setIsSendModalOpen(true) : undefined}
        addButtonLabel={
          canSend
            ? t("mailBroadcast.sendNewButton")
            : undefined
        }
      />
      <Table
        columns={columns}
        data={rows}
        isLoading={isLoading}
        getRowId={(row) => row.id}
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        emptyMessage={t("mailBroadcast.noData")}
      />
      <SendMailModal
        isOpen={isSendModalOpen}
        onClose={() => {
          setIsSendModalOpen(false);
          setPrefillTarget(undefined);
          setPrefillStaffIds(undefined);
          setPrefillAttachments(undefined);
        }}
        onSubmit={handleSendSubmit}
        initialTarget={prefillTarget}
        initialStaffIds={prefillStaffIds}
        initialAttachments={prefillAttachments}
        preparingAttachments={preparingAttachments}
      />
    </DashboardLayout>
  );
};

export default MailBroadcastPage;
